import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  ShieldCheck,
  Sparkles,
  Mail,
  ExternalLink,
  CheckCircle2,
  CreditCard,
  RefreshCw,
  Ticket,
} from 'lucide-react';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { BOX_IMAGES } from '@/data/images';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];

/** Derive the Deciplus /products URL from the same base as /reservation */
function buildBoutiqueUrl(baseUrl) {
  if (!baseUrl) return '';
  // Normalize: strip trailing slash and any existing suffix path
  const u = baseUrl.replace(/\/$/, '');
  // If the URL already ends with /products, keep it
  if (u.endsWith('/products')) return u;
  // If it ends with /calendar or /signIn etc., replace that segment
  return u.replace(/\/(calendar|products|signIn|signUp)$/, '') + '/products';
}

const WHAT_YOU_CAN_BUY = [
  {
    icon: CreditCard,
    title: 'Abonnement mensuel',
    desc: "Choisis ta formule (Illimité, 3×/semaine, 2×/semaine) et active-la en ligne. Prélèvement automatique chaque mois.",
  },
  {
    icon: Ticket,
    title: 'Carnets de séances',
    desc: "10 séances (149€), 5 séances (79€) ou Drop-in unique (18€) — parfait pour les rythmes variables ou les tests.",
  },
  {
    icon: Sparkles,
    title: 'Coaching individuel',
    desc: "Réserve tes séances de coaching personnalisé avec un coach certifié. Séance d'1h, programme sur-mesure.",
  },
];

export default function BoutiquePage() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content/reservation-config`)
      .then((r) => setCfg(r.data))
      .catch(() => setCfg(null));
  }, []);

  const boutiqueUrl = cfg?.embed_url ? buildBoutiqueUrl(cfg.embed_url) : '';
  const active = cfg?.enabled && boutiqueUrl;

  return (
    <>
      <PageMeta
        title="Acheter ton abonnement en ligne · CrossFit Saint-Chamond"
        description="Souscris ton abonnement CrossFit / HYROX ou achète ton carnet de séances en ligne, paiement sécurisé via Deciplus. Tarifs 59€ à 99€/mois sans engagement."
        path="/abonnements-en-ligne"
      />
      <BreadcrumbJsonLd
        items={[
          { name: 'Accueil', url: '/' },
          { name: 'Tarifs', url: '/tarifs' },
          { name: 'Acheter en ligne' },
        ]}
      />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/abonnements-en-ligne"
        eyebrow="Boutique en ligne"
        title="ACHÈTE TON ABONNEMENT"
        highlight="ABONNEMENT"
        subtitle="Abonnement mensuel, carnet de séances ou Drop-in : souscris en ligne en 2 minutes. Paiement sécurisé géré par Deciplus."
        image={BOX_IMAGES.dbSnatch}
        alt="Acheter un abonnement CrossFit Saint-Chamond en ligne"
        breadcrumbs={[
          { name: 'Accueil', href: '/' },
          { name: 'Tarifs', href: '/tarifs' },
          { name: 'Acheter en ligne' },
        ]}
        minHeight="min-h-[50vh]"
      />

      {/* ────────────────────── WHAT CAN BE BOUGHT ────────────────────── */}
      <section className="section-padding bg-white" data-testid="boutique-intro">
        <div className="container-custom">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 mb-14 items-end">
            <div>
              <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">
                ━━ Ce que tu peux acheter
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-dark mt-3 leading-[0.95]">
                TOUT SOUSCRIRE,{' '}
                <span className="text-gradient-primary italic font-display">
                  EN LIGNE
                </span>.
              </h2>
            </div>
            <p className="text-muted-foreground text-base leading-relaxed md:pb-3">
              Plus besoin de passer à la box pour payer : abonnement, Drop-in, carnet de séances ou coaching individuel — tout est dispo dans la boutique Deciplus ci-dessous. Paiement sécurisé, facture automatique.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {WHAT_YOU_CAN_BUY.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-5%' }}
                  transition={{ duration: 0.6, delay: idx * 0.1, ease: EASE }}
                  data-testid={`boutique-card-${idx}`}
                  className="p-6 bg-accent/40 border border-border/60 rounded-2xl hover:border-primary/40 hover:bg-white hover:shadow-[0_15px_50px_-15px_hsla(155,75%,18%,0.25)] transition-all duration-500"
                >
                  <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-heading text-lg text-dark mb-2 tracking-wide">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────────────────── BOUTIQUE IFRAME ────────────────────── */}
      <section
        className="relative pb-20 md:pb-28 bg-white"
        data-testid="boutique-widget-section"
      >
        {/* Dark top background */}
        <div className="absolute inset-x-0 top-0 h-[45%] bg-gradient-to-b from-dark via-dark to-primary-dark pointer-events-none" />
        <div className="absolute inset-x-0 top-10 mesh-gradient opacity-25 h-[35%] pointer-events-none" />

        <div className="container-custom relative z-10">
          <div className="flex justify-center mb-8">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-md border border-white/20 text-primary-glow text-xs font-bold tracking-[0.22em] uppercase"
              data-testid="boutique-live-badge"
            >
              <ShoppingBag className="w-3 h-3" />
              Boutique en ligne · Deciplus
            </span>
          </div>
          <h2 className="text-center font-heading text-3xl md:text-5xl text-white leading-[1.05] mb-3">
            SOUSCRIS EN{' '}
            <span className="text-gradient-primary italic font-display">
              2 MINUTES
            </span>
            .
          </h2>
          <p className="text-center text-white/65 text-sm md:text-base max-w-2xl mx-auto mb-10">
            La boutique ci-dessous est connectée à notre logiciel Deciplus. Ton paiement, ta facture et l'activation de ton abonnement sont instantanés.
          </p>

          {cfg === null ? (
            <div className="h-[800px] flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-center">
                <div className="animate-pulse text-white/40 text-sm tracking-[0.22em] uppercase mb-2">
                  Chargement de la boutique
                </div>
                <div className="flex gap-1 justify-center">
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            </div>
          ) : active ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="relative rounded-[24px] overflow-hidden border border-white/15 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.5)] bg-white ring-1 ring-primary/10"
              data-testid="boutique-iframe-wrapper"
            >
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                  </div>
                  <span className="hidden sm:inline text-xs text-muted-foreground tracking-wider font-mono truncate max-w-[280px] md:max-w-md">
                    {boutiqueUrl.replace('https://', '')}
                  </span>
                </div>
                <a
                  href={boutiqueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
                  data-testid="boutique-external-link"
                >
                  Ouvrir en plein écran
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <iframe
                src={boutiqueUrl}
                title="Boutique Deciplus — CrossFit Saint-Chamond"
                style={{ width: '100%', height: '1100px', border: 0, display: 'block' }}
                loading="lazy"
                allow="payment"
                data-testid="boutique-iframe"
              />
            </motion.div>
          ) : (
            <FallbackBoutique />
          )}

          {/* Reassurance strip */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {[
              { icon: ShieldCheck, label: 'Paiement sécurisé', sub: 'Carte bancaire via Stripe (Deciplus)' },
              { icon: CheckCircle2, label: 'Activation immédiate', sub: 'Accès aux cours dans la foulée' },
              { icon: RefreshCw, label: 'Sans engagement', sub: 'Résiliation avec préavis de 2 mois' },
              { icon: Sparkles, label: 'Facture automatique', sub: 'Reçue par email après paiement' },
            ].map((item, i) => (
              <div
                key={i}
                data-testid={`boutique-trust-${i}`}
                className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="h-4.5 w-4.5 text-primary" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-heading text-sm tracking-wide text-dark">
                    {item.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────── HESITATION? ────────────────────── */}
      <section className="section-padding bg-accent/40" data-testid="boutique-help">
        <div className="container-custom max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-dark leading-tight mb-4">
            Pas encore décidé ?{' '}
            <span className="text-gradient-primary italic font-display">
              On en discute.
            </span>
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed mb-8 max-w-2xl mx-auto">
            Tu hésites entre deux formules ? Tu veux profiter d'un essai gratuit avant de souscrire ?
            On te répond en moins de 24h — écris-nous ou passe à la box.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="boutique-contact-link">
              <Button
                size="lg"
                className="btn-shine bg-primary hover:bg-primary-light text-white font-heading tracking-wider text-sm px-7 py-6 rounded-xl glow-primary"
              >
                NOUS CONTACTER
              </Button>
            </Link>
            <Link to="/tarifs" data-testid="boutique-tarifs-link">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-border hover:border-primary text-dark font-heading tracking-wider text-sm px-7 py-6 rounded-xl"
              >
                VOIR LE DÉTAIL DES TARIFS
              </Button>
            </Link>
            <a href="mailto:crossfit.saintchamond@gmail.com">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-border hover:border-primary text-dark font-heading tracking-wider text-sm px-7 py-6 rounded-xl gap-2"
              >
                <Mail className="h-4 w-4" /> Email
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

function FallbackBoutique() {
  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md p-10 md:p-16 text-center"
      data-testid="boutique-fallback"
    >
      <div className="relative z-10 max-w-2xl mx-auto">
        <span className="inline-block text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase mb-4">
          ━━ Bientôt disponible
        </span>
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-5">
          Boutique en ligne{' '}
          <span className="text-gradient-primary italic font-display">
            en cours d'activation
          </span>
        </h2>
        <p className="text-white/70 leading-relaxed mb-8">
          En attendant, tu peux souscrire ton abonnement directement à la box, ou nous contacter pour choisir ta formule.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/contact">
            <Button className="bg-primary hover:bg-primary-dark text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg">
              Me contacter
            </Button>
          </Link>
          <Link to="/tarifs">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 hover:text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg"
            >
              Voir les tarifs
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
